import React from 'react';
import { Cart, User, Store, Coordinates, Close } from '@jbhi-fi/lanyard-ui/components/Icons';
import { HeaderUtilityButtonProps } from '../../HeaderLayout/components/HeaderUtilityButton/HeaderUtilityButton';
import { useNavigate } from '../../PlatformFunctionality';
import { useEffect, useRef, useState } from 'react';
import { useCustomerContext } from '../../context/CustomerContextProvider';
import { Coupon } from '../../types';
import { Tracker } from '../../Tracking';

export const MINICART_TOGGLE_EVENT = 'minicart-toggle-event';

const BUTTON_LABELS = {
  CART: 'Cart',
  STORES: 'Stores',
  TRACK_ORDER: 'Track order',
  ACCOUNT: {
    LOG_IN: 'Log in',
    CLOSE: 'Close',
  },
};

type HeaderUtilitiesProps = {
  totalCartQuantity?: number;
  newCouponCount?: number;
  coupons: Array<Coupon>;
  isPerks?: boolean;
  isCloseToBirthMonth?: boolean;
  shouldShowBirthdayField?: boolean;
  shouldShowPerksSignup?: boolean;
  MyAccountSubMenuComponent: React.FC<{ onHideSubmenu: () => void }>;
  onLoginClick: () => void;
};

export const useHeaderUtilities = ({
  totalCartQuantity,
  newCouponCount,
  coupons,
  isPerks,
  isCloseToBirthMonth,
  shouldShowBirthdayField,
  shouldShowPerksSignup,
  MyAccountSubMenuComponent,
  onLoginClick,
}: HeaderUtilitiesProps) => {
  const navigate = useNavigate();
  const [shouldCartBadgeAnimate, setShouldCartBadgeAnimate] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const { isLoggedIn, customerName } = useCustomerContext();
  const submenuRef = useRef<HTMLDivElement>(null);
  const hideSubmenuRef = useRef<(() => void) | null>(null);

  let couponValue = '';
  if (isCloseToBirthMonth) {
    couponValue = 'Birthday Coming Soon';
  } else if (shouldShowBirthdayField && coupons.length === 0) {
    couponValue = 'Enter birthday month';
  } else if (shouldShowPerksSignup) {
    couponValue = 'Join JB Perks';
  } else if (isPerks && coupons.length === 0) {
    couponValue = 'No Coupons Available';
  } else {
    couponValue = '';
  }

  const handleAccountClick = () => {
    setIsAccountOpen(!isAccountOpen);
    !isAccountOpen
      ? Tracker.trackCustomEvent({
          event: 'view_account_dropdown',
          coupon_count: coupons.length || 0,
          value: couponValue,
          coupon_name: coupons.map(coupon => coupon.content?.displayName).join(' | ') || '',
          coupon: coupons.map(coupon => coupon.couponName).join(' | ') || '',
          coupon_id: coupons.map(coupon => coupon.couponExternalId).join(' | ') || '',
        })
      : null;
    newCouponCount && newCouponCount > 0 ? Tracker.trackCustomEvent({ event: 'click_account_notification' }) : null;
  };

  const handleHideAccountSubMenu = (onHideSubmenu: () => void) => () => {
    setIsAccountOpen(false);
    onHideSubmenu();
  };

  const hasRendered = useRef(false);
  useEffect(() => {
    if (!hasRendered.current) {
      hasRendered.current = true;
      return;
    }
    setShouldCartBadgeAnimate(true);
    const badgeTimer = setTimeout(() => {
      setShouldCartBadgeAnimate(false);
    }, 1000);

    return () => clearTimeout(badgeTimer);
  }, [totalCartQuantity]);

  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const accountButton = document.getElementById('header-account');

      if (!submenuRef.current?.contains(event.target as Node) && !accountButton?.contains(event.target as Node)) {
        setIsAccountOpen(false);
        hideSubmenuRef.current?.();
      }
    };

    document.addEventListener('pointerdown', handleClickOutside);

    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
    };
  }, []);

  const headerUtilityData: Array<HeaderUtilityButtonProps> = [
    {
      label: BUTTON_LABELS.TRACK_ORDER,
      icon: Coordinates,
      datalayerValue: '/pages/track-my-order',
      testId: 'header-track-order',
      onClick: () => navigate('/pages/track-my-order'),
      datalayerLabel: BUTTON_LABELS.TRACK_ORDER,
    },
    {
      label: BUTTON_LABELS.STORES,
      onClick: () => navigate('/pages/store-finder'),
      icon: Store,
      testId: 'header-stores',
      datalayerValue: '/pages/store-finder',
      datalayerLabel: BUTTON_LABELS.STORES,
    },
    {
      label: isAccountOpen
        ? BUTTON_LABELS.ACCOUNT.CLOSE
        : isLoggedIn
        ? customerName ?? BUTTON_LABELS.ACCOUNT.LOG_IN
        : BUTTON_LABELS.ACCOUNT.LOG_IN,
      showOnMobile: true,
      newCouponCount: newCouponCount,
      hasSubmenu: isLoggedIn,
      submenuComponent: ({ onHideSubmenu }) => {
        hideSubmenuRef.current = onHideSubmenu;
        return (
          <div ref={submenuRef}>
            <MyAccountSubMenuComponent onHideSubmenu={handleHideAccountSubMenu(onHideSubmenu)} />
          </div>
        );
      },
      onClick: isLoggedIn
        ? () => {
            handleAccountClick();
            return;
          }
        : onLoginClick,
      icon: isAccountOpen ? Close : User,
      customElementId: 'header-account',
      testId: 'header-account',
      datalayerValue: '/account#/',
      datalayerLabel: isAccountOpen
        ? BUTTON_LABELS.ACCOUNT.CLOSE
        : isLoggedIn
        ? undefined
        : BUTTON_LABELS.ACCOUNT.LOG_IN,
    },
    {
      label: BUTTON_LABELS.CART,
      showOnMobile: true,
      shouldShowQtyBadge: Boolean(totalCartQuantity && totalCartQuantity > 0),
      badgeQty: totalCartQuantity,
      shouldBadgeAnimate: shouldCartBadgeAnimate,
      icon: Cart,
      customElementId: 'minicart-toggle',
      onClick: () => {
        const event = new CustomEvent(MINICART_TOGGLE_EVENT);
        window.dispatchEvent(event);
      },
      testId: 'minicart-toggle',
      datalayerValue: 'cart',
      datalayerLabel: BUTTON_LABELS.CART,
    },
  ];

  return headerUtilityData;
};
